import { z } from 'zod'

import { ProductType, ProductUnit } from '../types'

/**
 * An uploaded image, held whole rather than as a bare id: the id is what saves and
 * the url is what keeps the preview on screen after the file picker has closed.
 */
export const productImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  name: z.string(),
  type: z.string(),
})

/**
 * An option axis. Values are typed as one comma-separated line rather than a nested
 * array — an axis is two or three words in practice, and a repeater inside a repeater
 * costs the merchant more than it gives them.
 */
export const productOptionSchema = z.object({
  name: z.string().trim().min(1, 'Name this option.'),
  values: z.string().trim().min(1, 'List at least one value, separated by commas.'),
})

/**
 * What one unit costs to make or buy. Blank is "not known" and stays blank — it is
 * never read as zero, which would put every sale of the item at a 100% margin.
 *
 * Only the sign is checked: the API takes four decimals, and the field's format
 * stops at four, so a figure with more never reaches the schema.
 */
const costSchema = z.number().min(0, 'Cost cannot be negative.').optional()

/** What the cashier earns per unit sold. Blank is none; two decimals, like a price. */
const incentiveSchema = z.number().min(0, 'Incentive cannot be negative.').optional()

/**
 * The server validates a variant's attributes against the declared options and
 * rejects the product when the two do not line up, so every axis gets a value here.
 *
 * No stock: what a variant has in the drawer is inventory's to say, adjusted through
 * its own endpoint and its own screen. This form edits what the product *is*.
 */
export const productVariantSchema = z.object({
  id: z.string().optional(),
  sku: z.string().trim().min(2, 'A variant SKU needs at least 2 characters.'),
  gtin: z.string().trim().optional(),
  price: z.number({ invalid_type_error: 'Enter a price.' }).min(0, 'Price cannot be negative.'),
  cost: costSchema,
  incentive: incentiveSchema,
  thumbnail: productImageSchema.nullable(),
  attributes: z.array(
    z.object({
      name: z.string(),
      value: z.string().trim().min(1, 'Pick a value for every option.'),
    }),
  ),
})

export const productChoiceGroupSchema = z
  .object({
    choiceGroupId: z.string().min(1, 'Pick a choice group.'),
    minSelect: z.number({ invalid_type_error: 'Enter a number.' }).min(1, 'At least 1.'),
    maxSelect: z.number({ invalid_type_error: 'Enter a number.' }).min(1, 'At least 1.'),
  })
  .refine((group) => group.maxSelect >= group.minSelect, {
    message: 'Max cannot be below min.',
    path: ['maxSelect'],
  })

export const productSchema = z
  .object({
    id: z.string().optional(),
    thumbnail: productImageSchema.nullable(),
    bannerImages: z.array(productImageSchema),
    nameEn: z.string().trim().min(1, 'A product name is required.'),
    nameKm: z.string().trim().optional(),
    descriptionEn: z.string().trim().optional(),
    descriptionKm: z.string().trim().optional(),
    sku: z.string().trim().optional(),
    // The API rejects a one-character GTIN but accepts none at all, so an empty
    // box has to stay legal — it is cleared before the body goes up.
    gtin: z
      .string()
      .trim()
      .refine((value) => value === '' || value.length >= 2, 'A GTIN needs at least 2 characters.')
      .optional(),
    type: z.enum([ProductType.Stockable, ProductType.Consumable, ProductType.Composite]),
    unit: z.enum([ProductUnit.Unit, ProductUnit.Piece, ProductUnit.Kilogram]).optional(),
    price: z.number({ invalid_type_error: 'Enter a price.' }).min(0, 'Price cannot be negative.'),
    // The product's own, edited while it has no variants. Once it has them, each
    // variant carries its own beside its price and these boxes are put away.
    cost: costSchema,
    incentive: incentiveSchema,
    published: z.boolean(),
    categoryIds: z.array(z.string()),
    /** Optional — `null` is "no brand". Variants follow it and carry none of their own. */
    brandId: z.string().nullable(),
    tenantBranchIds: z.array(z.string()).min(1, 'Choose at least one branch.'),
    options: z.array(productOptionSchema),
    variants: z.array(productVariantSchema),
    choiceGroups: z.array(productChoiceGroupSchema),
  })
  .superRefine((values, ctx) => {
    // The server only writes children when there is more than one of them; a lone
    // variant is silently dropped, which reads as the form having lost it.
    if (values.variants.length === 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variants'],
        message: 'Add a second variant, or remove this one and price the product directly.',
      })
    }

    if (values.variants.length > 1 && values.options.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Variants need at least one option to vary along.',
      })
    }
  })

export type ProductFormValues = z.infer<typeof productSchema>
export type ProductVariantFormValues = z.infer<typeof productVariantSchema>
export type ProductOptionFormValues = z.infer<typeof productOptionSchema>
